import type { PopularityLike } from './electionBoardUtils'

export type VersusFighter = {
  id: string
  label: string
  subtitle?: string
  imageUrl?: string | null
  barColor: string
  stakePercent: number
  totalStake: number
  totalBets: number
  cornerSide?: 'red' | 'blue' | 'neutral' | null
}

export type VersusUndercardMatch = {
  id: string
  weightLabel?: string
  left: VersusFighter
  right: VersusFighter
}

export type VersusDisplayData = {
  mainEvent: { left: VersusFighter; right: VersusFighter } | null
  undercard: VersusUndercardMatch[]
  totalStake: number
  totalBets: number
}

const CORNER_COLORS = {
  red: '#EF4444',
  blue: '#3B82F6',
  neutral: '#6B7280',
} as const

function toFighter(sel: PopularityLike['selections'][number]): VersusFighter {
  const cornerSide = (sel as { cornerSide?: VersusFighter['cornerSide'] }).cornerSide ?? null
  let barColor = sel.barColor || '#3B82F6'
  if (!sel.barColor && cornerSide && CORNER_COLORS[cornerSide]) {
    barColor = CORNER_COLORS[cornerSide]
  }
  return {
    id: String(sel.selectionId),
    label: sel.label,
    subtitle: sel.subtitle,
    imageUrl: sel.imageUrl,
    barColor,
    stakePercent: sel.stakePercent,
    totalStake: sel.totalStake,
    totalBets: sel.totalBets ?? 0,
    cornerSide,
  }
}

function assignMainCorners(a: VersusFighter, b: VersusFighter): { left: VersusFighter; right: VersusFighter } {
  if (a.cornerSide === 'red' || b.cornerSide === 'blue') {
    return {
      left: a.cornerSide === 'red' ? a : b,
      right: b.cornerSide === 'blue' ? b : a.cornerSide === 'blue' ? a : b,
    }
  }
  if (b.cornerSide === 'red' || a.cornerSide === 'blue') {
    return {
      left: b.cornerSide === 'red' ? b : a,
      right: a.cornerSide === 'blue' ? a : b,
    }
  }
  return { left: a, right: b }
}

export type VersusLayoutMode = 'promo' | 'matchup'

export type VersusDisplayOptions = {
  layout?: VersusLayoutMode
  focus?: string
}

function pairFightersSequential(fighters: VersusFighter[]): Array<{ id: string; left: VersusFighter; right: VersusFighter; weightLabel?: string }> {
  const pairs: Array<{ id: string; left: VersusFighter; right: VersusFighter; weightLabel?: string }> = []
  for (let i = 0; i < fighters.length; i += 2) {
    const left = fighters[i]
    const right = fighters[i + 1] || placeholderFighter(`${left.id}-vs-tbd`, 'TBD', left.barColor)
    pairs.push({
      id: i === 0 ? 'main' : `undercard:${i / 2 - 1}`,
      left,
      right,
      weightLabel: left.subtitle?.match(/\d+\s*kg/i)?.[0] || undefined,
    })
  }
  return pairs
}

function withPairPercents(left: VersusFighter, right: VersusFighter): { left: VersusFighter; right: VersusFighter } {
  const sum = left.totalStake + right.totalStake
  if (sum <= 0) {
    return {
      left: { ...left, stakePercent: 50 },
      right: { ...right, stakePercent: 50 },
    }
  }
  const leftPct = Math.round((left.totalStake / sum) * 1000) / 10
  return {
    left: { ...left, stakePercent: leftPct },
    right: { ...right, stakePercent: Math.round((100 - leftPct) * 10) / 10 },
  }
}

function resolveFocusPair(
  pairs: Array<{ id: string; left: VersusFighter; right: VersusFighter }>,
  focus?: string,
) {
  const key = String(focus || 'main').trim().toLowerCase()
  if (key === 'main') return pairs[0] || null
  const match = /^undercard:(\d+)$/.exec(key)
  if (match) return pairs[1 + Number(match[1])] || null
  return pairs.find((p) => p.id === key) || pairs[0] || null
}

function pairTotals(left: VersusFighter, right: VersusFighter) {
  return {
    totalStake: left.totalStake + right.totalStake,
    totalBets: left.totalBets + right.totalBets,
  }
}

export function mapPopularityToVersusDisplay(
  popularity: PopularityLike,
  options: VersusDisplayOptions = {},
): VersusDisplayData {
  const fighters = popularity.selections.map(toFighter)
  const pairs = pairFightersSequential(fighters)
  const layout = options.layout || 'promo'

  if (layout === 'matchup') {
    const focused = resolveFocusPair(pairs, options.focus)
    if (!focused) {
      return { mainEvent: null, undercard: [], totalStake: 0, totalBets: 0 }
    }
    const assigned = assignMainCorners(focused.left, focused.right)
    const corners = withPairPercents(assigned.left, assigned.right)
    const totals = pairTotals(corners.left, corners.right)
    return {
      mainEvent: corners,
      undercard: [],
      totalStake: totals.totalStake,
      totalBets: totals.totalBets,
    }
  }

  let mainEvent: VersusDisplayData['mainEvent'] = null
  const undercard: VersusUndercardMatch[] = []

  if (pairs.length >= 1) {
    const assigned = assignMainCorners(pairs[0].left, pairs[0].right)
    mainEvent = withPairPercents(assigned.left, assigned.right)
  }

  for (let i = 1; i < pairs.length; i += 1) {
    const p = pairs[i]
    const assigned = assignMainCorners(p.left, p.right)
    const corners = withPairPercents(assigned.left, assigned.right)
    undercard.push({
      id: `${corners.left.id}-${corners.right.id}`,
      weightLabel: p.weightLabel,
      left: corners.left,
      right: corners.right,
    })
  }

  return {
    mainEvent,
    undercard,
    totalStake: popularity.totalStake,
    totalBets: popularity.totalBets,
  }
}

function placeholderFighter(id: string, label: string, barColor: string): VersusFighter {
  return {
    id,
    label,
    subtitle: 'รอประกาศ',
    imageUrl: null,
    barColor,
    stakePercent: 0,
    totalStake: 0,
    totalBets: 0,
    cornerSide: 'neutral',
  }
}

export function buildDemoVersusFromSelections(
  selections: Array<{
    key: string
    label: string
    subtitle?: string
    barColor?: string | null
    selectionCode?: string
    imageUrl?: string | null
    cornerSide?: 'red' | 'blue' | 'neutral' | null
  }>,
  seed = 1,
  options: VersusDisplayOptions = {},
): VersusDisplayData {
  const stats = buildDemoSelectionStats(selections, seed)
  const totalStake = stats.reduce((sum, row) => sum + row.totalStake, 0)
  const totalBets = stats.reduce((sum, row) => sum + row.totalBets, 0)
  return mapPopularityToVersusDisplay(
    { totalStake, totalBets, selections: stats },
    options,
  )
}

function buildDemoSelectionStats(
  selections: Array<{
    key: string
    label: string
    subtitle?: string
    barColor?: string | null
    selectionCode?: string
    imageUrl?: string | null
    cornerSide?: 'red' | 'blue' | 'neutral' | null
  }>,
  seed: number,
) {
  const wave = Math.sin(seed * 0.6) * 0.06
  const weights = selections.map((_, i) => Math.max(0.05, 0.5 + Math.sin((seed + i) * 0.7) * 0.15 - i * 0.04 + wave))
  const sum = weights.reduce((a, b) => a + b, 0)
  const totalStake = 324_500 + (seed % 9) * 8_200
  const totalBets = 1_856 + (seed % 5) * 43
  return selections.map((sel, i) => {
    const ratio = weights[i] / sum
    return {
      selectionId: sel.key,
      label: sel.label || 'Fighter',
      subtitle: sel.subtitle,
      barColor: sel.barColor || '#3B82F6',
      imageUrl: sel.imageUrl,
      cornerSide: sel.cornerSide,
      stakePercent: Math.round(ratio * 1000) / 10,
      totalStake: Math.round(totalStake * ratio),
      totalBets: Math.max(1, Math.round(totalBets * ratio)),
    }
  })
}

export function mainEventPercents(main: { left: VersusFighter; right: VersusFighter }) {
  const sum = main.left.stakePercent + main.right.stakePercent
  if (sum <= 0) return { left: 50, right: 50 }
  const left = Math.round((main.left.stakePercent / sum) * 1000) / 10
  return { left, right: Math.round((100 - left) * 10) / 10 }
}
